import { FC, useMemo, useState } from 'react'
import { ColorPicker, Input, Segmented, Select } from 'antd'
import type { Color } from 'antd/es/color-picker'
import pantoneSolidCoated from '../../data/pantone-solid-coated.json'
import technicalColors from '../../data/technical-colors.json'
import type { ColorInputMode, PaletteColor, UniversalColorValue } from '../../types/pantone-color'
import { filterOptionByLabel } from '../../helpers/filter-option-by-label'
import { techColorPickerLabel } from '../../helpers/technical-color-display'
import {
    HexRow,
    NameInputWrap,
    OptionRow,
    OptionSwatch,
    PantoneSelectWrap,
    Root,
} from './universal-color-picker.styles'

const pantoneColors = pantoneSolidCoated as PaletteColor[]
const colorPalette = technicalColors as PaletteColor[]

type PaletteOption = {
    value: string
    label: string
    hex: string
}

export type UniversalColorPickerProps = {
    value?: Pick<UniversalColorValue, 'name' | 'hex'> & { mode?: ColorInputMode }
    onChange?: (value: UniversalColorValue) => void
    disabled?: boolean
}

const emptyValue = (mode: ColorInputMode): UniversalColorValue => ({
    mode,
    name: '',
    hex: '',
})

const toOptions = (
    colors: PaletteColor[],
    labelFor: (name: string) => string
): PaletteOption[] =>
    colors.map(color => ({
        value: color.name,
        label: labelFor(color.name),
        hex: color.hex,
    }))

const UniversalColorPicker: FC<UniversalColorPickerProps> = ({ value, onChange, disabled }) => {
    const [mode, setMode] = useState<ColorInputMode>(value?.mode ?? 'hex')

    const pantoneOptions = useMemo(() => toOptions(pantoneColors, name => name), [])
    const colorOptions = useMemo(() => toOptions(colorPalette, techColorPickerLabel), [])

    const emit = (next: UniversalColorValue) => {
        onChange?.(next)
    }

    const onModeChange = (nextMode: ColorInputMode) => {
        setMode(nextMode)
        emit(emptyValue(nextMode))
    }

    const onHexColorChange = (color: Color) => {
        emit({
            mode: 'hex',
            name: value?.name ?? '',
            hex: color.toHexString(),
        })
    }

    const onHexNameChange = (name: string) => {
        emit({
            mode: 'hex',
            name,
            hex: value?.hex ?? '',
        })
    }

    const onPaletteChange = (paletteMode: 'pantone' | 'color', colors: PaletteColor[]) => (name?: string) => {
        if (!name) {
            emit(emptyValue(paletteMode))
            return
        }
        const found = colors.find(c => c.name === name)
        if (!found) {
            emit(emptyValue(paletteMode))
            return
        }
        emit({
            mode: paletteMode,
            name: found.name,
            hex: found.hex,
        })
    }

    const renderPaletteSelect = (
        paletteMode: 'pantone' | 'color',
        colors: PaletteColor[],
        options: PaletteOption[],
        placeholder: string
    ) => (
        <PantoneSelectWrap>
            <Select
                showSearch
                allowClear
                placeholder={placeholder}
                value={value?.name || undefined}
                options={options}
                optionRender={option => (
                    <OptionRow>
                        <OptionSwatch $hex={String(option.data.hex)} />
                        {option.data.label}
                    </OptionRow>
                )}
                labelRender={props => {
                    const found = colors.find(c => c.name === props.value)
                    if (!found) return props.label
                    const option = options.find(o => o.value === props.value)
                    return (
                        <OptionRow>
                            <OptionSwatch $hex={found.hex} />
                            {option?.label ?? found.name}
                        </OptionRow>
                    )
                }}
                filterOption={filterOptionByLabel}
                onChange={onPaletteChange(paletteMode, colors)}
                disabled={disabled}
            />
        </PantoneSelectWrap>
    )

    return (
        <Root>
            <Segmented
                options={[
                    { label: 'Pantone', value: 'pantone' },
                    { label: 'Color', value: 'color' },
                    { label: 'HEX', value: 'hex' },
                ]}
                value={mode}
                onChange={val => onModeChange(val as ColorInputMode)}
                disabled={disabled}
            />

            {mode === 'hex' && (
                <HexRow>
                    <ColorPicker
                        format='hex'
                        disabledFormat
                        showText
                        value={value?.hex || undefined}
                        onChange={onHexColorChange}
                        disabled={disabled}
                    />
                    <NameInputWrap>
                        <Input
                            placeholder='Наименование цвета'
                            value={value?.name ?? ''}
                            onChange={e => onHexNameChange(e.target.value)}
                            disabled={disabled}
                        />
                    </NameInputWrap>
                </HexRow>
            )}

            {mode === 'pantone' &&
                renderPaletteSelect('pantone', pantoneColors, pantoneOptions, 'Выберите Pantone')}

            {mode === 'color' &&
                renderPaletteSelect('color', colorPalette, colorOptions, 'Выберите цвет')}
        </Root>
    )
}

export default UniversalColorPicker
